import React, { type JSX } from 'react'
import styled from '@emotion/styled'
import { Link } from 'react-router'
import { ACTIVITY_STATEMENTS_ROUTE } from '@mobi/account/Areas/Statements/Utils/constants'
import { WITHDRAW_ROOT_ROUTE } from '@mobi/account/Areas/Withdraw/Utils'
import { routes as accountRoutes } from '@mobi/account/Utils'
import { spacing } from '@mobi/component-library/Foundations'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { SpinnerInlineStyled } from '@mobi/component-library/Legacy/Spinner/Spinner.styles'
import { PageHeader } from '@mobi/component-library/Navigation/PageHeader'
import { lessOrEqualThan } from '@mobi/component-library/Utils/media'
import { Constants, hexColors } from '@mobi/settings'
import { isReactNativeApp } from '@mobi/web-native-comms/web'
import { useAccountDetailsQuery } from '@core/Areas/Account/hooks/useGetAccountDetails'
import { openDeposit } from '@core/Areas/Deposit'
import { Button } from '@core/Components/Buttons/Button'
import { MainContainer } from '@core/Components/Containers/MainContainer'
import { Money } from '@core/Components/Text/Money'
import { keys } from '@core/Services/Analytics/keys'
import { trackEvent } from '@core/Services/Analytics/track'
import { state$ as userAccountState$ } from '@core/State/UserAccount/userAccountDriver'
import { useObservableImmutable } from '@core/Utils/hooks'
import { trackActivityStatementsSelected } from './helpers/analytics'

export const AccountBasePage = (): JSX.Element | null => {
  const [isLoading, setIsLoading] = React.useState(true)

  const { accountNumber, bonusBetBalance, bonusCashBalance, campaigns } = useObservableImmutable(
    userAccountState$,
    ['accountNumber', 'bonusBetBalance', 'bonusCashBalance', 'campaigns']
  )

  const { data: accountDetails } = useAccountDetailsQuery(true)
  const { balance, blackBookAlertCount = 0 } = accountDetails || {}

  React.useEffect(() => {
    if (balance) setIsLoading(false)
  }, [balance])

  const bonusCash = bonusCashBalance ?? 0
  const bonusBet = bonusBetBalance ?? 0
  const campaignsCount = campaigns?.count() ?? 0

  return (
    <MainContainer background='grey'>
      <PageHeader title='My Account' />

      <AccountBalanceStyled>
        <span data-tid-my-account-balance>
          {isLoading ? <SpinnerInlineStyled color='dark' /> : balance}
        </span>
        <Button
          data-tid-my-account-deposit-plus-button
          onClick={() => openDeposit()}
          aria-label='Deposit'
        >
          <Icon type='plus' size='2rem' />
        </Button>
      </AccountBalanceStyled>

      {!isLoading && (bonusBet > 0 || bonusCash > 0) && (
        <BonusBalanceStyled>
          {bonusBet > 0 && (
            <BonusItemStyled>
              <BonusBalanceTitleStyled>Bonus Bets</BonusBalanceTitleStyled>
              <BonusBalanceValueStyled>
                <Money amount={bonusBet} decimalPlaces={2} data-tid-my-bonusbet-balance />
              </BonusBalanceValueStyled>
            </BonusItemStyled>
          )}

          {bonusCash > 0 && (
            <BonusItemStyled>
              <BonusBalanceTitleStyled>Bonus Cash</BonusBalanceTitleStyled>
              <BonusBalanceValueStyled>
                <Money amount={bonusCash} decimalPlaces={2} data-tid-my-bonuscash-balance />
              </BonusBalanceValueStyled>
            </BonusItemStyled>
          )}
        </BonusBalanceStyled>
      )}

      <ListWrapperStyled>
        <SubHeadingStyled>Racing</SubHeadingStyled>
        <ListStyled>
          <ItemStyled>
            <LinkStyled to='/account/activity/pending' data-tid-my-account-pending>
              <Icon type='pending' /> Pending Bets
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to='/account/activity/results' data-tid-my-account-resulted>
              <Icon type='resulted' /> Resulted Bets
            </LinkStyled>
          </ItemStyled>
        </ListStyled>
      </ListWrapperStyled>

      <ListWrapperStyled>
        <SubHeadingStyled>Sports</SubHeadingStyled>
        <ListStyled>
          <ItemStyled>
            <LinkStyled to='/tabtouch-sports/bethistory/open' data-tid-my-account-pending>
              <Icon type='pending' /> Pending Bets
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to='/tabtouch-sports/bethistory/settled' data-tid-my-account-resulted>
              <Icon type='resulted' /> Resulted Bets
            </LinkStyled>
          </ItemStyled>
        </ListStyled>
      </ListWrapperStyled>

      <ListWrapperStyled>
        <SubHeadingStyled>Account</SubHeadingStyled>
        <ListStyled>
          <ItemStyled>
            <LinkButtonStyled
              data-tid-my-account-deposit
              to=''
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                e.preventDefault()
                openDeposit()
              }}
            >
              <Icon type='plus' /> Deposit
            </LinkButtonStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to={WITHDRAW_ROOT_ROUTE} data-tid-my-account-withdrawal>
              <Icon type='minus' /> Withdraw
            </LinkStyled>
          </ItemStyled>

          {campaignsCount > 0 && (
            <ItemStyled>
              <LinkStyled to='/account/offer' data-tid-my-account-bonus>
                <Icon type='offer' /> Bonuses <BadgeStyled>{campaignsCount}</BadgeStyled>
              </LinkStyled>
            </ItemStyled>
          )}

          <ItemStyled>
            <LinkStyled
              to={`/${ACTIVITY_STATEMENTS_ROUTE}`}
              onClick={() => trackActivityStatementsSelected(accountNumber as number)}
            >
              <Icon type='statement' /> Transactions & Statements
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to='/account/blackbook' data-tid-my-account-blackbook>
              <Icon type='blackbook' /> Blackbook{' '}
              {blackBookAlertCount > 0 && <BadgeStyled>{blackBookAlertCount}</BadgeStyled>}
            </LinkStyled>
          </ItemStyled>
        </ListStyled>
      </ListWrapperStyled>

      <ListWrapperStyled>
        <SubHeadingStyled>Responsible Wagering</SubHeadingStyled>
        <ListStyled>
          <ItemStyled>
            <LinkStyled to='/account/deposit-limits' data-tid-my-account-deposit-limits>
              <Icon type='depositLimits' /> Deposit Limit
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to='/account/precommitment' data-tid-my-account-bet-limits>
              <Icon type='errorCircled' /> Bet Limit
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to='/account/take-a-break' data-tid-my-account-take-a-break>
              <Icon type='calendar' /> Take a Break
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to='/account/close' data-tid-my-account-close-account>
              <Icon type='closeAccount' /> Close Account
            </LinkStyled>
          </ItemStyled>

          <ItemStyled>
            <LinkStyled to={accountRoutes.blockDebitCard} data-tid-my-account-block-debit-card>
              <Icon type='errorCircled' /> Block Debit Card
            </LinkStyled>
          </ItemStyled>
        </ListStyled>
      </ListWrapperStyled>

      <ListWrapperStyled>
        <SubHeadingStyled>Details</SubHeadingStyled>
        <ListStyled>
          <ItemStyled>
            <LinkStyled to='/account/change-password' data-tid-my-account-change-password>
              <Icon type='asterisk' /> Change Password
            </LinkStyled>
          </ItemStyled>
          <ItemStyled>
            <LinkStyled to='/account/settings' data-tid-my-account-settings>
              <Icon type='personMinimal' /> Settings
            </LinkStyled>
          </ItemStyled>
          <ItemStyled>
            <LinkStyled to={accountRoutes.personalDetails} data-tid-my-account-contact-details>
              <Icon type='phone' /> Personal Details
            </LinkStyled>
          </ItemStyled>
          <ItemStyled>
            <LinkStyled to='/account/marketingcommunications' data-tid-my-account-subscription>
              <Icon type='envelopeOpen' /> Subscription
            </LinkStyled>
          </ItemStyled>
          {isReactNativeApp && (
            <ItemStyled>
              <LinkStyled to='/account/biometrics' data-tid-my-account-biometrics>
                <Icon type='depositLimits' /> Enable PIN/Biometrics
              </LinkStyled>
            </ItemStyled>
          )}
          <ItemStyled>
            <LinkStyled to='/contactus' data-tid-my-account-contactus>
              <Icon type='tabtouchLogoCompact' /> Contact Us
            </LinkStyled>
          </ItemStyled>
          <ItemStyled>
            <LinkStyled
              onClick={() => {
                trackEvent(keys.setPageVisit, { customURL: '/', pageTitle: 'HELP' })
              }}
              to='https://help.tabtouch.com.au/'
              data-tid-my-account-help
              rel='noopener'
              target='_blank'
            >
              <Icon type='infoCircled' /> Help & Info
            </LinkStyled>
          </ItemStyled>
        </ListStyled>
      </ListWrapperStyled>

      <p style={{ marginBottom: 0, paddingBottom: spacing.md }}>
        Gamble responsibly. <br /> Call Gambling Help on {Constants.GamblingHelpPhoneNumber}.
      </p>
    </MainContainer>
  )
}

// ======
// Styles
// ======

const ListWrapperStyled = styled.div({
  padding: '0 0.5rem',
  marginBottom: '1.5rem',
})

const ListStyled = styled.ul({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',

  margin: 0,
  padding: 0,
  listStyleType: 'none',
})

const ItemStyled = styled.li({
  width: 'calc(50% - 0.5px)',
  marginBottom: '1px',
})

const LinkStyled = styled(Link)({
  display: 'flex',
  alignItems: 'center',
  height: '5rem',
  margin: 0,
  padding: 0,

  fontWeight: 'bold',
  fontSize: '1.3rem',
  textDecoration: 'none',
  lineHeight: 1,
  color: hexColors.nightRider,
  background: hexColors.white,
  boxShadow: '0 1px 1px rgba(0,0,0,0.1)',

  '& > svg': {
    fontSize: '2rem',
    margin: '0 0.5rem',
    color: hexColors.lola,
  },

  [lessOrEqualThan('px350')]: {
    fontSize: '1.2rem',
  },
})

const LinkButtonStyled = styled(LinkStyled)({
  width: '100%',
  border: 0,
}).withComponent('button')

const AccountBalanceStyled = styled.div({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  padding: '2rem 0 2rem 0.6rem',
  fontSize: '3rem',
  fontWeight: 'bold',

  '& > button': {
    padding: '0.5rem',
    marginLeft: '1rem',
  },
})

const BonusBalanceStyled = styled.div({
  display: 'flex',
  flexDirection: 'row',
  paddingBottom: '2rem',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '7em',
})

const BonusItemStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  flex: '1',
})

const BonusBalanceTitleStyled = styled.div({
  textTransform: 'uppercase',
  fontSize: '1.3rem',
  fontWeight: 'bold',
  lineHeight: 1.2,
  color: hexColors.grey,
})

const BonusBalanceValueStyled = styled.div({
  color: hexColors.black,
  fontSize: '2rem',
  fontWeight: 'bold',
  paddingTop: '0.2em',
})

const BadgeStyled = styled.span<{ isMaxCount?: boolean }>(
  {
    display: 'inline-block',
    marginLeft: 'auto',
    marginRight: '0.5rem',
    width: '2rem',
    minWidth: '2rem',
    height: '2rem',
    borderRadius: '50%',
    lineHeight: '2rem',
    textAlign: 'center',
    backgroundColor: hexColors.freeSpeechRed,
    color: hexColors.white,
  },
  ({ isMaxCount = false }) => ({
    fontSize: isMaxCount ? '1rem' : '1.2rem',
    ...(isMaxCount && {
      '&::after': {
        content: '"+"',
        letterSpacing: '-2px',
      },
    }),
  })
)

const SubHeadingStyled = styled.div({
  color: hexColors.nero,
  fontSize: '1.3rem',
  fontWeight: 'bold',
  paddingBottom: '0.5rem',
})
